import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with proper conflict resolution.
 * Combines clsx (conditional classes) with tailwind-merge (deduplication).
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number as USD currency (no decimals).
 * Returns '$0' for null/undefined values.
 */
export function formatCurrency(val: number | null | undefined): string {
  if (val === null || val === undefined) return '$0'
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

/**
 * Format an ISO date string for display.
 * Returns '—' for null/undefined values.
 */
export function formatDate(dateStr: string | null | undefined, includeTime = false): string {
  if (!dateStr) return '—'
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  if (includeTime) {
    options.hour = 'numeric'
    options.minute = '2-digit'
  }
  return new Date(dateStr).toLocaleDateString('en-US', options)
}

/**
 * Capitalize the first letter of a string.
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Format seconds as "m:ss" duration string.
 * Returns '—' for null/undefined values; 0 is formatted as "0:00".
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

/**
 * Format a SCREAMING_SNAKE_CASE enum value for display, e.g.
 * SALES_INQUIRY -> "Sales Inquiry", UNANALYZED -> "Unanalyzed".
 * Returns '—' for null/undefined values.
 */
export function prettyEnum(value: string | null | undefined): string {
  if (!value) return '—'
  return value
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Format a yyyy-MM-dd date-only string pinned to UTC. The generic formatDate
 * renders in the viewer's local timezone, which shifts UTC-midnight dates
 * back a day west of UTC. Pass Intl options to control which parts render
 * (defaults to "Jul 1, 2026").
 */
export function formatDateOnlyUTC(
  dateStr: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string {
  const d = new Date(`${dateStr}T00:00:00Z`)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' })
}

/**
 * Format a yyyy-MM snapshot month for display, e.g. "2026-07" -> "July 2026".
 * UTC-safe like formatDateOnlyUTC (pins to the 1st so no local-timezone
 * shift can move it into the neighboring month). Returns '—' for malformed
 * input.
 */
export function formatMonthLabel(month: string | null | undefined): string {
  if (!month) return '—'
  const d = new Date(`${month}-01T00:00:00Z`)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Format an ISO timestamp as a short relative phrase ("3 hours ago").
 * Falls back to formatDate beyond a week; returns '—' for null/undefined.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (isNaN(then)) return '—'
  const diffMs = Date.now() - then
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
  return formatDate(iso)
}
